import { PROFILE_CONTENT_SX, PROFILE_LABEL_SX } from "../components/profileFieldStyles";
import AIAgentCard from "../components/sidebar-widgets/AIAgentCard";
import CompanyTableCell from "../components/CompanyTableCell";
import TeamMemberRecords from "../components/TeamMemberRecords";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Tab,
  TableCell,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Breadcrumb from "../components/Breadcrumb";
import TabSectionHeader from "../components/TabSectionHeader";
import GenericDataTable from "../components/GenericDataTable";
import AddUserDialog from "../components/PopupWindows/AddUserDialog";
import ShareJobDialog from "../components/ShareJobDialog";
import JobNotesCard from "../components/sidebar-widgets/JobNotesCard";
import LocationSearchableField from "../components/LocationSearchableField";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { EmailOutlined as EmailOutlinedIcon } from "../components/icons/FigmaControlIcons";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { DeleteOutlineRounded as DeleteOutlineRoundedIcon } from "../components/icons/FigmaControlIcons";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { mergeStoredUser, readStoredUser } from "../utils/userSession";
import { clearStoredAuth } from "../controllers/authController";
import { formatLocalDateTime } from "../utils/dateTime";
import {
  buildPublicSharePayload,
  createAndCopyPublicShareLink,
} from "../utils/publicShareLinks";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../utils/pillButtonStyles";

const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";
const BORDER = "#D9E0EA";
const STATUS_OPTIONS = ["Active", "Available", "Interviewing", "Not Available", "Inactive"];
const ENGAGEMENT_OPTIONS = ["RPO", "Contingency", "Retained", "Contract", "Permanent", "Interim", "Consulting"];
const DOCUMENT_TYPES = [
  { value: "resume", label: "Resume / CV" },
  { value: "reference", label: "Reference" },
  { value: "other", label: "Other" },
];
const cleanText = (value) => String(value ?? "").trim();
const toStringList = (value) => {
  if (Array.isArray(value)) return value.map(cleanText).filter(Boolean);
  const text = cleanText(value);
  return text ? text.split(",").map(cleanText).filter(Boolean) : [];
};
const getInitials = (name) =>
  cleanText(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "R";
const listRows = (payload, keys) => {
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
    if (Array.isArray(payload?.data?.[key])) return payload.data[key];
  }
  return Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.rows) ? payload.rows : [];
};
const uniqueOptions = (values) => Array.from(new Set(values.map(cleanText).filter(Boolean))).sort((a, b) => a.localeCompare(b));

const quickActionSx = {
  borderRadius: 999,
  px: 2,
  py: 0.72,
  minHeight: 38,
  textTransform: "none",
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  bgcolor: "#9747FF",
  boxShadow: "none",
  "&:hover": { bgcolor: "#7C3AED", boxShadow: "none" },
};
const sidebarWidgetButtonSx = [
  pillButtonInteractiveSx,
  pillButtonFilledSx,
  {
    px: 2.5,
    py: 0.8,
    fontWeight: 400,
    fontSize: 14,
    boxShadow: "none",
    "&:hover .MuiButton-startIcon svg, &:focus .MuiButton-startIcon svg, &:focus-visible .MuiButton-startIcon svg": {
      fill: "currentColor !important",
      stroke: "none !important",
    },
    "&:hover .MuiButton-startIcon svg *, &:focus .MuiButton-startIcon svg *, &:focus-visible .MuiButton-startIcon svg *": {
      fill: "currentColor !important",
      stroke: "none !important",
    },
  },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 1, height: 38 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
  "& .MuiInputBase-input": { fontSize: 13, py: 0.75 },
};

const StatCard = ({ value, label }) => (
  <Box sx={{ flex: "1 1 145px", minWidth: 145, border: `1px solid ${PURPLE}`, borderRadius: 1.5, px: 2, py: 1.25 }}>
    <Typography sx={{ fontSize: 25, lineHeight: 1.15, color: "#475467", fontWeight: 700 }}>{value}</Typography>
    <Typography sx={{ mt: 1, fontSize: 13, color: "#667085" }}>{label}</Typography>
  </Box>
);

const FieldRow = ({ label, children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "155px minmax(0, 1fr)" }, alignItems: "center", gap: 2, mb: 4 }}>
    <Typography className="profile-field-label" sx={PROFILE_LABEL_SX}>{label}</Typography>
    {children}
  </Box>
);

const PROFILE_TABS = ["details", "candidates", "clients", "jobs", "notifications", "activities", "leads", "companies", "contacts", "pipeline"];

export default function UserView() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const documentInputRef = useRef(null);
  const tab = Math.max(0, PROFILE_TABS.indexOf(new URLSearchParams(location.search).get("tab")));
  const setTab = (value) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", PROFILE_TABS[value] || "details");
    navigate({ pathname: location.pathname, search: params.toString() }, { state: location.state });
  };
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [profileSource, setProfileSource] = useState(() => location.state?.recruiter || null);
  const [saveState, setSaveState] = useState({ error: "", success: "", saving: false });
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ open: false, password: "", confirmPassword: "" });
  const [passwordState, setPasswordState] = useState({ error: "", success: "", saving: false });
  const [teamActivities, setTeamActivities] = useState([]);
  const [teamActivitiesLoading, setTeamActivitiesLoading] = useState(false);
  const [teamActivitiesRefreshKey, setTeamActivitiesRefreshKey] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [shareProfileOpen, setShareProfileOpen] = useState(
    () => Boolean(location.state?.openShareProfile)
  );
  const [shareProfileMessage, setShareProfileMessage] = useState("");
  const [shareRecipientOptions, setShareRecipientOptions] = useState({ jobs: [], companies: [], contacts: [] });
  const documentType = "resume";
  const [documentUploadState, setDocumentUploadState] = useState({ error: "", uploading: false });
  const [documentDeleteId, setDocumentDeleteId] = useState("");
  const [assignedJobs, setAssignedJobs] = useState(null);
  const [assignedJobsState, setAssignedJobsState] = useState({ loading: false, error: "" });
  const recruiter = profileSource || location.state?.recruiter;
  const viewingUser = useMemo(() => readStoredUser(), []);
  const isAdministrator = ["admin", "administrator", "system_user"].includes(
    cleanText(viewingUser?.role).toLowerCase()
  );
  const isTeamMemberView =
    location.pathname.startsWith("/team/") ||
    Boolean(recruiter?.engagementType || recruiter?.engagement_type);
  const details = useMemo(() => {
    const source = recruiter || {};
    const name = cleanText(source?.name) || cleanText(source?.full_name) || cleanText(source?.email) || "Recruiter";
    const parts = name.split(/\s+/);
    const role = cleanText(source?.role) || "Recruiter";
    return {
      name,
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" "),
      title: cleanText(source?.title) || role,
      email: cleanText(source?.email),
      phone: cleanText(source?.phone),
      location: cleanText(source?.location),
      role,
      avatar: cleanText(source?.profile_image || source?.avatar_url),
      id: cleanText(source?.id || source?.user_id || id),
      activities: Number(source?.activities_count || source?.activities || 0) || 0,
      assignments: Number(source?.assignments_count || source?.assignments || 0) || 0,
      status: cleanText(source?.status) || "Active",
      about: cleanText(source?.about),
      industryExpertise: toStringList(source?.industry_expertise ?? source?.industryExpertise ?? source?.industry),
      rolesWorked: toStringList(source?.roles_worked ?? source?.rolesWorked),
      engagementTypes: toStringList(source?.engagement_types ?? source?.engagementTypes ?? source?.engagement_type ?? source?.engagementType),
      jobsWorkedHistory: Array.isArray(source?.jobs_worked_history) ? source.jobs_worked_history : [],
      documents: Array.isArray(source?.documents) ? source.documents : [],
    };
  }, [id, recruiter]);
  const profile = { ...details, ...draft };
  const isOwnProfile = String(details.id) === String(viewingUser?.id);
  // Administrators can edit every team member's core profile fields. Previously this
  // only allowed administrators to edit candidates, so recruiter fields appeared
  // editable while rejecting all typing.
  const canEditProfile = isOwnProfile || isAdministrator;
  const canEditProfessionalProfile = isTeamMemberView && (isOwnProfile || isAdministrator);
  const assignmentCount = Array.isArray(assignedJobs) ? assignedJobs.length : details.assignments;
  const teamJobColumns = useMemo(() => [
    { id: "title", label: "Job Title", minWidth: 210, renderCell: (job) => <Box sx={{ minWidth: 0 }}><Typography component="button" type="button" onClick={() => navigate(`/jobs?jobId=${encodeURIComponent(String(job.id))}&tab=details`)} noWrap sx={{ p: 0, border: 0, bgcolor: "transparent", color: "#344054", font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", "&:hover": { color: PURPLE, textDecoration: "underline" }, "&:focus-visible": { outline: `2px solid ${PURPLE}`, outlineOffset: 2, borderRadius: 0.5 } }}>{job.title || "-"}</Typography><Typography sx={{ mt: 0.2, color: "#98A2B3", fontSize: 11.5 }}>Job ID: {job.id}</Typography></Box> },
    { id: "company_name", label: "Company Name", minWidth: 170, renderCell: (job) => <CompanyTableCell row={job} /> },
    { id: "status", label: "Status", minWidth: 110, renderCell: (job) => <Chip label={job.status || "-"} size="small" sx={{ height: 24, borderRadius: "999px", bgcolor: job.status?.toLowerCase() === "open" ? "#ECFDF3" : "#F2F4F7", color: job.status?.toLowerCase() === "open" ? "#027A48" : "#475467", fontSize: 11, fontWeight: 700 }} /> },
    { id: "priority", label: "Priority", minWidth: 105, renderCell: (job) => <Typography sx={{ color: "#475467", fontSize: 14 }}>{job.priority || "-"}</Typography> },
    { id: "location", label: "Location", minWidth: 150, renderCell: (job) => <Typography noWrap sx={{ color: "#475467", fontSize: 14 }}>{job.location || "-"}</Typography> },
    { id: "owner", label: "Owner", minWidth: 150, renderCell: (job) => <Typography noWrap sx={{ color: "#344054", fontSize: 14, fontWeight: 600 }}>{job.owner || "-"}</Typography> },
  ], [navigate]);
  const renderTeamJobRow = useCallback((job) => <>{teamJobColumns.map((column) => <TableCell key={column.id} sx={{ minWidth: column.minWidth }}>{column.renderCell ? column.renderCell(job) : job[column.id]}</TableCell>)}</>, [teamJobColumns]);
  const updateProfileField = (field) => (event) =>
    setDraft((current) => ({ ...current, [field]: event.target.value }));

  const notificationAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || "";
    const rawToken = String(token).replace(/^Bearer\s+/i, "").trim();
    return rawToken ? { Authorization: `Bearer ${rawToken}`, "x-access-token": rawToken } : {};
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!isOwnProfile) return;
    setNotificationsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/notifications?limit=100`, {
        headers: notificationAuthHeaders(),
        credentials: "include",
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok) setNotifications(Array.isArray(payload?.notifications) ? payload.notifications : []);
    } finally {
      setNotificationsLoading(false);
    }
  }, [isOwnProfile, notificationAuthHeaders]);

  const markNotificationRead = async (notificationId) => {
    setNotifications((current) => current.map((item) =>
      String(item.id) === String(notificationId) ? { ...item, is_read: 1 } : item
    ));
    await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/notifications/${encodeURIComponent(String(notificationId))}/read`, {
      method: "POST",
      headers: notificationAuthHeaders(),
      credentials: "include",
    }).catch(() => loadNotifications());
  };

  const markAllNotificationsRead = async () => {
    setNotifications((current) => current.map((item) => ({ ...item, is_read: 1 })));
    await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/notifications/read-all`, {
      method: "POST",
      headers: notificationAuthHeaders(),
      credentials: "include",
    }).catch(() => loadNotifications());
  };

  const handleShareProfile = async ({ emails = [], recipients = {}, notes = "", documents = [] } = {}) => {
    const namedRecipients = [
      ...(recipients?.jobs || []),
      ...(recipients?.companies || []),
      ...(recipients?.contacts || []),
    ];
    const recipientCount = [...emails, ...namedRecipients].length;
    if (!recipientCount) {
      throw new Error("Choose at least one job, company, contact, or email recipient.");
    }

    const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
    const shareDocuments = [];
    for (const document of documents) {
      if (document instanceof File) {
        const formData = new FormData();
        formData.append("document", document);
        formData.append("document_type", "other");
        const response = await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/users/${encodeURIComponent(details.id)}/documents`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || `Unable to upload ${document.name}.`);
        const uploaded = Array.isArray(payload?.documents) ? payload.documents : [];
        const matchingDocument = uploaded.find((item) => cleanText(item?.name) === cleanText(document.name));
        if (matchingDocument?.url) shareDocuments.push(matchingDocument);
        setProfileSource((current) => ({ ...(current || {}), documents: uploaded }));
      } else if (document?.url) {
        shareDocuments.push(document);
      }
    }

    const sharePayload = buildPublicSharePayload("team_member", {
      id: details.id,
      name: details.name,
      title: details.role,
      status: details.status,
      location: details.location,
      role: details.role,
      about: profile.about,
      industryExpertise: profile.industryExpertise,
      rolesWorked: profile.rolesWorked,
      jobsWorkedHistory: profile.jobsWorkedHistory,
      engagementType: profile.engagementTypes?.join(", "),
      assignments: details.assignments,
      placements: recruiter?.placements,
    });
    sharePayload.fields = [
      ...sharePayload.fields,
      ...(notes ? [{ label: "Note", value: cleanText(notes).slice(0, 180) }] : []),
      ...shareDocuments.slice(0, 3).map((document) => ({ label: "Document", value: cleanText(document.name || document.filename || "Professional document") })),
    ];
    const apiBase = String(
      process.env.REACT_APP_API_BASE || (typeof window !== "undefined" ? window.location.origin : "")
    ).replace(/\/$/, "");
    sharePayload.documents = shareDocuments.map((document) => ({
      ...document,
      url: /^https?:\/\//i.test(cleanText(document?.url))
        ? cleanText(document.url)
        : `${apiBase}${cleanText(document?.url)}`,
    }));
    await createAndCopyPublicShareLink({
      apiBase: process.env.REACT_APP_API_BASE || "",
      payload: sharePayload,
    });
    setShareProfileMessage(
      `Professional profile link copied for ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}. Direct contact details are excluded.`
    );
  };

  useEffect(() => {
    if (!shareProfileOpen) return;
    let active = true;
    const loadOptions = async () => {
      const headers = notificationAuthHeaders();
      const readJson = async (url) => {
        const response = await fetch(url, { credentials: "include", headers });
        return response.ok ? response.json().catch(() => ({})) : {};
      };
      const base = process.env.REACT_APP_API_BASE || "";
      const [jobsPayload, companiesPayload, contactsPayload] = await Promise.all([
        readJson(`${base}/api/jobs?limit=300`),
        readJson(`${base}/api/companies?page=1&limit=5000&compact=1`),
        readJson(`${base}/api/hiring-managers?limit=500`),
      ]);
      if (!active) return;
      setShareRecipientOptions({
        jobs: uniqueOptions(listRows(jobsPayload, ["jobs", "rows"]).map((job) => [job?.job_title || job?.title, job?.company_name || job?.company].filter(Boolean).join(" — "))),
        companies: uniqueOptions(listRows(companiesPayload, ["companies", "rows"]).map((company) => company?.name || company?.company_name)),
        contacts: uniqueOptions(listRows(contactsPayload, ["hiringManagers", "contacts", "rows"]).map((contact) => [contact?.name || contact?.full_name, contact?.company_name || contact?.company].filter(Boolean).join(" — "))),
      });
    };
    loadOptions().catch(() => { if (active) setShareRecipientOptions({ jobs: [], companies: [], contacts: [] }); });
    return () => { active = false; };
  }, [notificationAuthHeaders, shareProfileOpen]);

  useEffect(() => {
    const userId = cleanText(id);
    const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
    if (!userId || !token) return undefined;

    let active = true;
    fetch(`${process.env.REACT_APP_API_BASE || ""}/api/users/${encodeURIComponent(userId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || "Unable to load user.");
        return payload?.user;
      })
      .then((savedUser) => {
        if (active && savedUser) setProfileSource((current) => ({ ...(current || {}), ...savedUser }));
      })
      .catch(() => {});

    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (!isTeamMemberView || !details.id) return undefined;
    const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
    let active = true;
    setAssignedJobsState({ loading: true, error: "" });
    fetch(`${process.env.REACT_APP_API_BASE || ""}/api/team-members/${encodeURIComponent(details.id)}/assignments`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || "Unable to load assigned jobs.");
        return Array.isArray(payload?.jobs) ? payload.jobs : [];
      })
      .then((jobs) => {
        if (!active) return;
        setAssignedJobs(jobs);
        setAssignedJobsState({ loading: false, error: "" });
      })
      .catch((error) => {
        if (!active) return;
        setAssignedJobs([]);
        setAssignedJobsState({ loading: false, error: error?.message || "Unable to load assigned jobs." });
      });
    return () => { active = false; };
  }, [details.id, isTeamMemberView]);

  useEffect(() => {
    if (!isTeamMemberView || !details.id) {
      setTeamActivities([]);
      return undefined;
    }

    let active = true;
    setTeamActivitiesLoading(true);
    fetch(
      `${process.env.REACT_APP_API_BASE || ""}/api/activities?entityType=user&entityId=${encodeURIComponent(details.id)}`,
      { headers: notificationAuthHeaders(), credentials: "include" }
    )
      .then(async (response) => {
        if (!response.ok) throw new Error("Unable to load team member activities.");
        return response.json();
      })
      .then((payload) => {
        if (active) setTeamActivities(Array.isArray(payload?.activities) ? payload.activities : []);
      })
      .catch(() => {
        if (active) setTeamActivities([]);
      })
      .finally(() => {
        if (active) setTeamActivitiesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [details.id, isTeamMemberView, notificationAuthHeaders, teamActivitiesRefreshKey]);

  useEffect(() => {
    if (tab !== 4 || !isOwnProfile) return undefined;
    loadNotifications();
  }, [tab, isOwnProfile, loadNotifications]);

  const handleSave = async () => {
    const nextRole = cleanText(draft.role);
    setSaveState({ error: "", success: "", saving: true });
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
      let savedUser = {};
      if (canEditProfile) {
        const name = [cleanText(draft.firstName ?? details.firstName), cleanText(draft.lastName ?? details.lastName)]
          .filter(Boolean)
          .join(" ");
        const response = await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/users/${encodeURIComponent(details.id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name, email: cleanText(draft.email ?? details.email), title: cleanText(draft.title ?? details.title), phone: cleanText(draft.phone ?? details.phone), location: cleanText(draft.location ?? details.location) }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || "Unable to save your profile.");
        savedUser = payload?.user || {};
      }
      if (isAdministrator && nextRole && nextRole !== details.role) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/users/${encodeURIComponent(details.id)}/role`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ role: nextRole }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || "Unable to save the user role.");
        savedUser = { ...savedUser, ...(payload?.user || {}) };
      }
      if (canEditProfessionalProfile) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/users/${encodeURIComponent(details.id)}/professional-profile`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            status: cleanText(draft.status ?? details.status),
            about: cleanText(draft.about ?? details.about),
            industry_expertise: toStringList(draft.industryExpertise ?? details.industryExpertise),
            roles_worked: toStringList(draft.rolesWorked ?? details.rolesWorked),
            engagement_types: toStringList(draft.engagementTypes ?? details.engagementTypes),
            jobs_worked_history: draft.jobsWorkedHistory ?? details.jobsWorkedHistory,
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || "Unable to save the professional profile.");
        const professionalProfile = payload?.professionalProfile || {};
        savedUser = {
          ...savedUser,
          ...professionalProfile,
          industryExpertise: professionalProfile.industry_expertise || [],
          rolesWorked: professionalProfile.roles_worked || [],
          engagementTypes: professionalProfile.engagement_types || [],
          jobsWorkedHistory: professionalProfile.jobs_worked_history || [],
        };
      }
      setProfileSource((current) => ({ ...(current || {}), ...savedUser }));
      if (String(savedUser.id) === String(viewingUser?.id)) mergeStoredUser(savedUser);
      setDraft({});
      setIsEditing(false);
      setSaveState({ error: "", success: "Profile saved.", saving: false });
    } catch (error) {
      setSaveState({ error: error?.message || "Unable to save the user role.", success: "", saving: false });
    }
  };

  const updateProfessionalList = (field) => (_event, values) => {
    setDraft((current) => ({ ...current, [field]: toStringList(values) }));
  };
  const updateJobHistory = (index, field, value) => {
    setDraft((current) => {
      const jobsWorkedHistory = [...(current.jobsWorkedHistory ?? details.jobsWorkedHistory)];
      jobsWorkedHistory[index] = { ...jobsWorkedHistory[index], [field]: value };
      return { ...current, jobsWorkedHistory };
    });
  };
  const addJobHistory = () => {
    setDraft((current) => ({
      ...current,
      jobsWorkedHistory: [
        ...(current.jobsWorkedHistory ?? details.jobsWorkedHistory),
        { id: `job-${Date.now()}`, company: "", title: "", job_type: "", industry: "", engagement_type: "", start_date: "", end_date: "", summary: "" },
      ],
    }));
  };
  const removeJobHistory = (index) => {
    setDraft((current) => ({
      ...current,
      jobsWorkedHistory: (current.jobsWorkedHistory ?? details.jobsWorkedHistory).filter((_item, itemIndex) => itemIndex !== index),
    }));
  };
  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setDocumentUploadState({ error: "", uploading: true });
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
      const formData = new FormData();
      formData.append("document", file);
      formData.append("document_type", documentType);
      const response = await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/users/${encodeURIComponent(details.id)}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Unable to upload the document.");
      setProfileSource((current) => ({ ...(current || {}), documents: payload?.documents || [] }));
      setDocumentUploadState({ error: "", uploading: false });
    } catch (error) {
      setDocumentUploadState({ error: error?.message || "Unable to upload the document.", uploading: false });
    }
  };

  const handleDocumentDelete = async (document) => {
    const documentId = cleanText(document?.id);
    if (!documentId || !details.id || documentDeleteId) return;
    if (!window.confirm(`Delete this document?\n\n${document?.name || "Document"}`)) return;

    setDocumentUploadState((current) => ({ ...current, error: "" }));
    setDocumentDeleteId(documentId);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE || ""}/api/users/${encodeURIComponent(details.id)}/documents/${encodeURIComponent(documentId)}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Unable to delete the document.");
      setProfileSource((current) => ({ ...(current || {}), documents: payload?.documents || [] }));
    } catch (error) {
      setDocumentUploadState((current) => ({ ...current, error: error?.message || "Unable to delete the document." }));
    } finally {
      setDocumentDeleteId("");
    }
  };

  const handlePasswordSave = async () => {
    if (passwordForm.password.length < 6) {
      setPasswordState({ error: "Password must be at least 6 characters.", success: "", saving: false });
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setPasswordState({ error: "Passwords do not match.", success: "", saving: false });
      return;
    }

    setPasswordState({ error: "", success: "", saving: true });
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE || ""}/api/users/${encodeURIComponent(details.id)}/password`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ password: passwordForm.password }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Unable to update password.");
      setPasswordForm({ open: false, password: "", confirmPassword: "" });
      clearStoredAuth();
      navigate("/", { replace: true, state: { message: payload.message || "Password updated. Please log in again." } });
    } catch (error) {
      setPasswordState({ error: error?.message || "Unable to update password.", success: "", saving: false });
    }
  };

  const tabEmptyText =
    tab === 1
      ? "No candidates are linked to this team member yet."
      : tab === 2
      ? "No clients are linked to this team member yet."
      : tab === 3
      ? "No jobs are linked to this team member yet."
      : "No team member activity has been recorded yet.";

  return (
    <Box sx={{ ...PROFILE_CONTENT_SX, py: 0, pb: 3, minWidth: 0 }}>
      <Breadcrumb
        activeLabel={details.name}
        appendActiveLabel
        items={[
          { label: "Overview", onClick: () => navigate("/overview") },
          { label: "Team", path: "/team" },
        ]}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1fr) 440px" }, gap: 2 }}>
        <Box sx={{ bgcolor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", p: 2 }}>
            <StatCard value={assignmentCount} label="Assignments" />
            <StatCard value={isTeamMemberView ? teamActivities.length : details.activities} label="Activities" />
            <StatCard value="0" label="Skills" />
            <StatCard value="0" label="Experience" />
            <StatCard value="0" label="Education" />
          </Box>

          <Divider />
          <Box sx={{ px: { xs: 2, md: 4 }, py: 2.75 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography data-popup-section sx={{ fontSize: 22, fontWeight: 800, color: BLUE }}>{isTeamMemberView ? "Team Member" : "Recruiter Profile:"} {details.name}</Typography>
              {isAdministrator ? <Typography sx={{ fontSize: 12, color: "#667085" }}>Role: <Box component="span" sx={{ color: PURPLE }}>{details.role}</Box></Typography> : null}
            </Box>

            {isTeamMemberView ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, minHeight: 38 }}>
                <Typography sx={{ fontSize: 13, color: "#667085" }}>Status</Typography>
                {isEditing && canEditProfessionalProfile ? (
                  <TextField select size="small" value={profile.status} onChange={updateProfileField("status")} sx={{ minWidth: 170, ...fieldSx }}>
                    {STATUS_OPTIONS.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                  </TextField>
                ) : (
                  <Chip label={profile.status || "Active"} size="small" sx={{ borderRadius: 1, bgcolor: profile.status === "Not Available" || profile.status === "Inactive" ? "#FEF3F2" : "#ECFDF3", color: profile.status === "Not Available" || profile.status === "Inactive" ? "#B42318" : "#027A48", fontWeight: 700 }} />
                )}
              </Box>
            ) : null}
            <Tabs variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile value={tab} onChange={(_, value) => setTab(value)} sx={{ mt: isTeamMemberView ? 0.5 : 1.5, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", color: PURPLE, fontSize: 14, px: 1.75 }, "& .Mui-selected": { fontWeight: 700 } }} TabIndicatorProps={{ style: { backgroundColor: BLUE, height: 2 } }}>
              <Tab label="Details" />
              <Tab label="Candidates" />
              <Tab label="Clients" />
              <Tab label={`Jobs ${assignmentCount ? assignmentCount : ""}`} />
              <Tab label={`Notifications ${notifications.filter((item) => !Number(item.is_read)).length || ""}`} />
              <Tab label={`Activities ${(isTeamMemberView ? teamActivities.length : details.activities) || ""}`} />
              {isTeamMemberView && <Tab label="Leads" value={6} />}
              {isTeamMemberView && <Tab label="Companies" value={7} />}
              {isTeamMemberView && <Tab label="Contacts" value={8} />}
              {isTeamMemberView && <Tab label="Pipeline" value={9} />}
            </Tabs>
            <Divider />

            {tab === 0 ? (
              <Box sx={{ pt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mb: 1.5 }}>
                  {isEditing ? <Button onClick={() => { setDraft({}); setIsEditing(false); setSaveState({ error: "", success: "", saving: false }); }} sx={{ color: "#667085", textTransform: "none", fontSize: 13 }}>Cancel</Button> : null}
                  {isOwnProfile ? <Button onClick={() => { setPasswordForm((current) => ({ ...current, open: !current.open })); setPasswordState({ error: "", success: "", saving: false }); }} sx={{ color: PURPLE, textTransform: "none", fontSize: 13, fontWeight: 600 }}>Change password</Button> : null}
                  {isAdministrator ? <Button onClick={() => setAddUserOpen(true)} sx={{ ...quickActionSx, minHeight: 34, px: 1.75, py: 0.45, fontSize: 13 }}>Add new user</Button> : null}
                  {(canEditProfile || canEditProfessionalProfile || isAdministrator) ? <Button disabled={saveState.saving} onClick={() => isEditing ? handleSave() : setIsEditing(true)} sx={{ color: PURPLE, textTransform: "none", fontSize: 13, fontWeight: 600 }}>{saveState.saving ? "Saving..." : isEditing ? "Save" : "Edit"}</Button> : null}
                </Box>
                {saveState.error ? <Typography sx={{ mb: 1.5, fontSize: 13, color: "#B42318" }}>{saveState.error}</Typography> : null}
                {saveState.success ? <Typography sx={{ mb: 1.5, fontSize: 13, color: "#027A48" }}>{saveState.success}</Typography> : null}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.15, mb: 3 }}>
                  <Avatar src={details.avatar || undefined} sx={{ width: 46, height: 46, bgcolor: "#E9D7FE", color: PURPLE, fontWeight: 800 }}>{getInitials(details.name)}</Avatar>
                  <Box><Typography sx={{ fontSize: 14, fontWeight: 700, color: "#344054" }}>{details.name}</Typography><Typography sx={{ fontSize: 12, color: "#98A2B3" }}>{details.role}</Typography></Box>
                </Box>
                <FieldRow label="Name"><Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}><TextField value={profile.firstName} onChange={updateProfileField("firstName")} InputProps={{ readOnly: !isEditing || !canEditProfile }} sx={fieldSx} /><TextField value={profile.lastName} onChange={updateProfileField("lastName")} InputProps={{ readOnly: !isEditing || !canEditProfile }} sx={fieldSx} /></Box></FieldRow>
                <FieldRow label="Email address"><TextField fullWidth value={profile.email} onChange={updateProfileField("email")} placeholder="—" InputProps={{ readOnly: !isEditing || !canEditProfile }} sx={fieldSx} /></FieldRow>
                {isAdministrator ? <FieldRow label="Role"><TextField select fullWidth value={profile.role === "candidate" ? "" : profile.role} onChange={updateProfileField("role")} InputProps={{ readOnly: !isEditing }} sx={fieldSx}><MenuItem value="" disabled>Choose a platform role</MenuItem><MenuItem value="administrator">Administrator</MenuItem><MenuItem value="system_user">System</MenuItem><MenuItem value="recruiter">Recruiter</MenuItem></TextField></FieldRow> : <FieldRow label="Role"><TextField fullWidth value={profile.role} InputProps={{ readOnly: true }} sx={fieldSx} /></FieldRow>}
                {isOwnProfile && passwordForm.open ? <FieldRow label="New password"><Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}><TextField fullWidth type="password" autoComplete="new-password" placeholder="New password" value={passwordForm.password} onChange={(event) => setPasswordForm((current) => ({ ...current, password: event.target.value }))} sx={fieldSx} /><TextField fullWidth type="password" autoComplete="new-password" placeholder="Confirm password" value={passwordForm.confirmPassword} onChange={(event) => setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }))} sx={fieldSx} /><Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" }, display: "flex", alignItems: "center", gap: 1 }}><Button disabled={passwordState.saving} onClick={handlePasswordSave} sx={{ color: PURPLE, textTransform: "none", fontSize: 13, fontWeight: 700 }}>{passwordState.saving ? "Updating..." : "Save password"}</Button>{passwordState.error ? <Typography sx={{ fontSize: 13, color: "#B42318" }}>{passwordState.error}</Typography> : null}{passwordState.success ? <Typography sx={{ fontSize: 13, color: "#027A48" }}>{passwordState.success}</Typography> : null}</Box></Box></FieldRow> : null}
                <FieldRow label="Phone number"><TextField fullWidth value={profile.phone} onChange={updateProfileField("phone")} placeholder="—" InputProps={{ readOnly: !isEditing || !canEditProfile }} sx={fieldSx} /></FieldRow>
                <FieldRow label="Location">
                  {isEditing && canEditProfile ? (
                    <LocationSearchableField
                      value={profile.location}
                      onChange={(nextLocation) => setDraft((current) => ({ ...current, location: nextLocation }))}
                      onInputValueChange={(nextLocation) => setDraft((current) => ({ ...current, location: nextLocation }))}
                      placeholder="Search city, state, or country"
                      textFieldProps={{ fullWidth: true, autoComplete: "off", sx: fieldSx }}
                    />
                  ) : (
                    <TextField fullWidth value={profile.location} placeholder="—" InputProps={{ readOnly: true }} sx={fieldSx} />
                  )}
                </FieldRow>
                {isTeamMemberView ? (
                  <>
                    <TabSectionHeader
                      title="Professional Profile"
                      rightText={canEditProfessionalProfile ? (isEditing ? "Save" : "Edit") : ""}
                      onAction={() => isEditing ? handleSave() : setIsEditing(true)}
                      dividerColor="#D6E9FF"
                      actionSx={{ color: PURPLE, fontWeight: 500 }}
                      sx={{ mt: 3, mb: 3 }}
                    />
                    <FieldRow label="Industry Expertise">
                      <Autocomplete multiple freeSolo options={[]} value={profile.industryExpertise} onChange={updateProfessionalList("industryExpertise")} readOnly={!isEditing || !canEditProfessionalProfile} renderInput={(params) => <TextField {...params} placeholder={profile.industryExpertise.length ? "" : "Add industries"} sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { minHeight: 38, height: "auto" } }} />} />
                    </FieldRow>
                    <FieldRow label="Roles Worked">
                      <Autocomplete multiple freeSolo options={[]} value={profile.rolesWorked} onChange={updateProfessionalList("rolesWorked")} readOnly={!isEditing || !canEditProfessionalProfile} renderInput={(params) => <TextField {...params} placeholder={profile.rolesWorked.length ? "" : "Add job types or roles"} sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { minHeight: 38, height: "auto" } }} />} />
                    </FieldRow>
                    <FieldRow label="Engagement Type/s">
                      <Autocomplete multiple freeSolo options={ENGAGEMENT_OPTIONS} value={profile.engagementTypes} onChange={updateProfessionalList("engagementTypes")} readOnly={!isEditing || !canEditProfessionalProfile} renderInput={(params) => <TextField {...params} placeholder={profile.engagementTypes.length ? "" : "Select engagement types"} sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { minHeight: 38, height: "auto" } }} />} />
                    </FieldRow>
                    <FieldRow label="About">
                      <TextField fullWidth multiline minRows={4} value={profile.about} onChange={updateProfileField("about")} placeholder="Professional summary, specialties, and experience" InputProps={{ readOnly: !isEditing || !canEditProfessionalProfile }} sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { height: "auto" } }} />
                    </FieldRow>

                    <TabSectionHeader
                      title="Jobs Worked History"
                      actionNode={isEditing && canEditProfessionalProfile ? <Button startIcon={<AddOutlinedIcon />} onClick={addJobHistory} sx={{ color: PURPLE, textTransform: "none", fontSize: 13, fontWeight: 700 }}>Add Experience</Button> : null}
                      dividerColor="#D6E9FF"
                      sx={{ mt: 3, mb: 3 }}
                    />
                    {!profile.jobsWorkedHistory.length ? <Typography sx={{ py: 2, fontSize: 13, color: "#98A2B3" }}>No previous professional experience has been added.</Typography> : null}
                    <Box sx={{ display: "grid", gap: 1.5 }}>
                      {profile.jobsWorkedHistory.map((job, index) => (
                        <Box key={job.id || index} sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, p: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.25, flex: 1 }}>
                              {[['company', 'Company'], ['title', 'Title'], ['job_type', 'Job Type'], ['industry', 'Industry'], ['engagement_type', 'Engagement Type']].map(([field, label]) => <TextField key={field} label={label} value={job[field] || ""} onChange={(event) => updateJobHistory(index, field, event.target.value)} InputProps={{ readOnly: !isEditing || !canEditProfessionalProfile }} sx={fieldSx} />)}
                              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}><TextField label="Start" type="month" value={job.start_date || ""} onChange={(event) => updateJobHistory(index, "start_date", event.target.value)} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditing || !canEditProfessionalProfile }} sx={fieldSx} /><TextField label="End" type="month" value={job.end_date || ""} onChange={(event) => updateJobHistory(index, "end_date", event.target.value)} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditing || !canEditProfessionalProfile }} sx={fieldSx} /></Box>
                              <TextField label="Experience Summary" multiline minRows={2} value={job.summary || ""} onChange={(event) => updateJobHistory(index, "summary", event.target.value)} InputProps={{ readOnly: !isEditing || !canEditProfessionalProfile }} sx={{ ...fieldSx, gridColumn: { md: "1 / -1" }, "& .MuiOutlinedInput-root": { height: "auto" } }} />
                            </Box>
                            {isEditing && canEditProfessionalProfile ? <IconButton aria-label="Remove experience" onClick={() => removeJobHistory(index)} sx={{ color: "#B42318" }}><DeleteOutlineRoundedIcon /></IconButton> : null}
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    <TabSectionHeader
                      title="Documents"
                      rightText={canEditProfessionalProfile ? (isEditing ? "Save" : "Edit") : ""}
                      onAction={() => isEditing ? handleSave() : setIsEditing(true)}
                      dividerColor="#D6E9FF"
                      actionSx={{ color: PURPLE, fontWeight: 500 }}
                      sx={{ mt: 3, mb: 3 }}
                    />
                    <input ref={documentInputRef} type="file" hidden accept=".pdf,.doc,.docx,.txt,.rtf" onChange={handleDocumentUpload} />
                    {documentUploadState.error ? <Typography sx={{ mb: 1.5, fontSize: 13, color: "#B42318" }}>{documentUploadState.error}</Typography> : null}
                    {profile.documents.length ? <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {profile.documents.map((document) => {
                        const documentId = cleanText(document?.id);
                        const isDeleting = documentDeleteId === documentId;
                        const documentTypeLabel = DOCUMENT_TYPES.find((type) => type.value === document.document_type)?.label || "Other";
                        const sizeLabel = Number(document?.size) ? ` - ${Math.max(1, Math.round(Number(document.size) / 1024))} KB` : "";
                        return <Box key={documentId || document.url} sx={{ display: "flex", alignItems: "center", gap: 1, border: `1px solid ${BORDER}`, borderRadius: 2, p: 1, bgcolor: "#fff", flex: "0 1 600px", maxWidth: "100%", minWidth: 0, opacity: isDeleting ? 0.6 : 1 }}>
                          <Box component={document.url ? "a" : "div"} href={document.url || undefined} target={document.url ? "_blank" : undefined} rel="noreferrer" sx={{ width: 34, height: 34, borderRadius: 2, border: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#F8FAFC", color: BLUE, flex: "0 0 auto" }}><DescriptionOutlinedIcon sx={{ fontSize: 18 }} /></Box>
                          <Box component={document.url ? "a" : "div"} href={document.url || undefined} target={document.url ? "_blank" : undefined} rel="noreferrer" sx={{ minWidth: 0, flex: 1, color: "inherit", textDecoration: "none" }}><Typography noWrap sx={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>{document.name || "Document"}</Typography><Typography sx={{ fontSize: 11, color: "#667085" }}>{documentTypeLabel}{sizeLabel}</Typography></Box>
                          {canEditProfessionalProfile ? <IconButton aria-label={`Delete ${document.name || "document"}`} onClick={() => handleDocumentDelete(document)} size="small" disabled={isDeleting} sx={{ color: "#EF4444" }}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton> : null}
                        </Box>;
                      })}
                    </Box> : null}
                    {canEditProfessionalProfile ? <Box sx={{ mt: 1.5 }}><Button variant="outlined" size="small" startIcon={<AddOutlinedIcon />} disabled={documentUploadState.uploading} onClick={() => documentInputRef.current?.click()} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 400, color: PURPLE, borderColor: PURPLE, height: 40, minHeight: 40, px: 2.2, "&:hover": { borderColor: PURPLE } }}>{documentUploadState.uploading ? "Uploading..." : "Add More"}</Button></Box> : null}
                  </>
                ) : null}
              </Box>
            ) : isTeamMemberView && (tab === 1 || tab === 2) ? (
              <TeamMemberRecords memberId={details.id} view={tab === 1 ? "candidates" : "companies"} />
            ) : isTeamMemberView && tab >= 6 ? (
              <TeamMemberRecords memberId={details.id} view={{ 6: "leads", 7: "companies", 8: "contacts", 9: "pipeline" }[tab]} />
            ) : tab === 3 && isTeamMemberView ? (
              <Box sx={{ py: 2 }}>
                {assignedJobsState.loading ? <Typography sx={{ py: 3, fontSize: 14, color: "#667085" }}>Loading assigned jobs…</Typography> : null}
                {assignedJobsState.error ? <Typography sx={{ py: 2, fontSize: 13, color: "#B42318" }}>{assignedJobsState.error}</Typography> : null}
                {!assignedJobsState.loading && !assignedJobsState.error && !assignedJobs?.length ? <Typography sx={{ py: 3, fontSize: 14, color: "#667085" }}>No jobs are linked to this team member yet.</Typography> : null}
                {!assignedJobsState.loading && assignedJobs?.length ? <GenericDataTable columns={teamJobColumns} rows={assignedJobs} renderRow={renderTeamJobRow} defaultOrderBy="title" defaultOrderDirection="asc" includeSelectionColumn={false} initialRowsPerPage={10} visualVariant="company-standard" tableSx={{ "& .MuiTableHead-root .MuiTableCell-root:first-of-type, & .MuiTableBody-root .MuiTableCell-root:first-of-type": { pl: 2 } }} /> : null}
              </Box>
            ) : tab === 4 ? (
              <Box sx={{ py: 2 }}>
                {!isOwnProfile ? (
                  <Typography sx={{ py: 3, fontSize: 14, color: "#667085" }}>
                    Notifications are available only on your own profile.
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1.25 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#344054" }}>Notification Center</Typography>
                      <Button onClick={markAllNotificationsRead} disabled={!notifications.some((item) => !Number(item.is_read))} sx={{ color: PURPLE, textTransform: "none", fontSize: 13, fontWeight: 700 }}>Mark all as read</Button>
                    </Box>
                    {notificationsLoading ? <Typography sx={{ py: 3, fontSize: 13, color: "#667085" }}>Loading notifications…</Typography> : null}
                    {!notificationsLoading && !notifications.length ? <Typography sx={{ py: 3, fontSize: 13, color: "#98A2B3" }}>You have no notifications.</Typography> : null}
                    {notifications.map((notification) => (
                      <Box key={notification.id} onClick={() => !Number(notification.is_read) && markNotificationRead(notification.id)} sx={{ cursor: Number(notification.is_read) ? "default" : "pointer", px: 1.5, py: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: Number(notification.is_read) ? "transparent" : "#F9F5FF" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#344054" }}>{notification.title}</Typography>
                        {notification.body ? <Typography sx={{ mt: 0.35, fontSize: 12.5, color: "#667085", whiteSpace: "pre-wrap" }}>{notification.body}</Typography> : null}
                        <Typography sx={{ mt: 0.55, fontSize: 11.5, color: "#98A2B3" }}>
                          {notification.created_at
                            ? formatLocalDateTime(notification.created_at, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                              })
                            : ""}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            ) : tab === 5 && isTeamMemberView ? (
              <Box sx={{ py: 2 }}>
                {teamActivitiesLoading ? <Typography sx={{ py: 3, fontSize: 14, color: "#667085" }}>Loading activities…</Typography> : null}
                {!teamActivitiesLoading && !teamActivities.length ? <Typography sx={{ py: 3, fontSize: 14, color: "#98A2B3" }}>No team member activity has been recorded yet.</Typography> : null}
                {!teamActivitiesLoading && teamActivities.map((activity) => (
                  <Box key={activity.id} sx={{ py: 1.5, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#344054" }}>{activity.title || "Activity"}</Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#475467", whiteSpace: "pre-wrap" }}>{activity.details || activity.summary || "—"}</Typography>
                    <Typography sx={{ mt: 0.7, fontSize: 12, color: "#98A2B3" }}>{formatLocalDateTime(activity.created_at, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</Typography>
                  </Box>
                ))}
              </Box>
            ) : <Typography sx={{ py: 5, fontSize: 14, color: "#667085" }}>{tabEmptyText}</Typography>}
          </Box>
        </Box>

        <Box sx={{ display: "grid", alignContent: "start", gap: 2 }}>
          <Box sx={{ bgcolor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 2, p: 3 }}>
            <Typography data-popup-section sx={{ fontSize: 18, fontWeight: 800, color: BLUE, mb: 2 }}>Quick Actions</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button className={withPillButtonClassName()} variant="contained" startIcon={<ShareOutlinedIcon />} onClick={() => setShareProfileOpen(true)} sx={sidebarWidgetButtonSx}>Share</Button>
              <Button className={withPillButtonClassName()} variant="contained" startIcon={<PhoneOutlinedIcon />} sx={sidebarWidgetButtonSx}>Call</Button>
              <Button className={withPillButtonClassName()} variant="contained" startIcon={<EmailOutlinedIcon />} sx={sidebarWidgetButtonSx}>Email</Button>
              <Button className={withPillButtonClassName()} variant="contained" startIcon={<CalendarMonthOutlinedIcon />} sx={sidebarWidgetButtonSx}>Schedule</Button>
              <Button className={withPillButtonClassName()} variant="contained" startIcon={<WorkOutlineRoundedIcon />} sx={sidebarWidgetButtonSx}>Assign to Job</Button>
            </Box>
          </Box>
          {isTeamMemberView ? (
            <JobNotesCard
              apiBase={process.env.REACT_APP_API_BASE || ""}
              entityId={details.id}
              entityType="user"
              activities={teamActivities}
              onRefresh={() => setTeamActivitiesRefreshKey((current) => current + 1)}
              onOpenAllNotes={() => setTab(5)}
            />
          ) : (
            <AIAgentCard entityType="recruiter" entityId={details.id} entityName={details.name}
              promptText="Review this recruiter profile and suggest next steps."
              noteText="Ask about activity, suitable job assignments, or profile improvements." linkText="" />
          )}
        </Box>
      </Box>
      <AddUserDialog open={addUserOpen} onClose={() => setAddUserOpen(false)} user={viewingUser} />
      <ShareJobDialog
        open={shareProfileOpen}
        onClose={() => setShareProfileOpen(false)}
        onSubmit={handleShareProfile}
        apiBase={process.env.REACT_APP_API_BASE || ""}
        resetKey={`team-member-${details.id}`}
        title="Share Team Member Profile"
        sectionTitle="Share professional profile with"
        submitLabel="Share Profile"
        summaryItems={[details.name, details.role, details.location].filter(Boolean)}
        defaultDocuments={profile.documents}
        showEmailField
        recipientFields={[
          { key: "jobs", label: "Job", placeholder: "Choose a job", options: shareRecipientOptions.jobs },
          { key: "companies", label: "Company", placeholder: "Choose a company", options: shareRecipientOptions.companies },
          { key: "contacts", label: "Contact or Hiring Manager", placeholder: "Choose a contact", options: shareRecipientOptions.contacts },
        ]}
      />
      {shareProfileMessage ? <Box sx={{ position: "fixed", right: 24, bottom: 24, zIndex: 1400, maxWidth: 420, px: 2, py: 1.25, borderRadius: 2, bgcolor: "#344054", color: "#fff", fontSize: 13, boxShadow: "0 12px 32px rgba(16,24,40,0.2)" }}>{shareProfileMessage}</Box> : null}
    </Box>
  );
}
